import { useCallback, useEffect, useRef, useState } from "react";

interface UseDocumentPanOptions {
  /**
   * When true, disable panning entirely. Use this for modes where the
   * page already attaches its own click-drag handlers — e.g. line-item
   * or field-item rule drawing in the verification cockpit. Setting
   * `disabled` lets the existing handler win without us competing for
   * the mouse stream.
   */
  disabled?: boolean;
}

interface UseDocumentPanReturn<T extends HTMLElement> {
  /** Attach to your scrolling container. */
  ref: React.MutableRefObject<T | null>;
  /** Spread onto the same container's JSX. */
  panHandlers: { onMouseDown: (e: React.MouseEvent) => void };
  /** True while the user is actively dragging. Use it for cursor swap. */
  isPanning: boolean;
}

/**
 * Click-and-drag pan for any scrolling container (e.g. the document
 * pane in Final Checker / Verification Cockpit).
 *
 * On mousedown over the container's empty area we record the cursor +
 * scroll position; on mousemove we apply the delta to `scrollLeft` /
 * `scrollTop` against those baselines. mouseup ends the drag.
 *
 * Clicks on interactive children short-circuit so existing handlers
 * win:
 *   • `data-interaction="true"` (SVG markers, resize handles, bbox)
 *   • `<button>` / `<a>` / `<input>` / `<textarea>` / `<select>`
 *
 * If the container has no scroll room (everything fits inside its
 * client box), the drag is also short-circuited — otherwise we'd swap
 * to grabbing cursor and nothing visible would happen, which feels
 * broken.
 *
 * Only the primary mouse button triggers the pan.
 */
export function useDocumentPan<T extends HTMLElement = HTMLDivElement>(
  opts: UseDocumentPanOptions = {},
): UseDocumentPanReturn<T> {
  const { disabled = false } = opts;
  const ref = useRef<T | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const startRef = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // Window-level move/up while panning so the drag survives the cursor
  // leaving the container (matching native scrollbar behaviour).
  useEffect(() => {
    if (!isPanning) return;
    const handleMove = (e: MouseEvent) => {
      const el = ref.current;
      if (!el) return;
      const dx = e.clientX - startRef.current.x;
      const dy = e.clientY - startRef.current.y;
      el.scrollLeft = startRef.current.scrollLeft - dx;
      el.scrollTop = startRef.current.scrollTop - dy;
    };
    const handleUp = () => setIsPanning(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    // Cancel on ESC too — symmetry with the fullscreen toggle.
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsPanning(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("keydown", handleKey);
    };
  }, [isPanning]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      if (e.button !== 0) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Skip interactive descendants so their own handlers win.
      if (target.closest('[data-interaction="true"]')) return;
      if (target.closest("button")) return;
      if (target.closest("a")) return;
      if (target.closest("input, textarea, select")) return;

      const el = ref.current;
      if (!el) return;

      // If the content already fits, panning is pointless and the
      // cursor swap reads as a bug.
      if (
        el.scrollWidth <= el.clientWidth &&
        el.scrollHeight <= el.clientHeight
      ) {
        return;
      }

      startRef.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: el.scrollLeft,
        scrollTop: el.scrollTop,
      };
      setIsPanning(true);
    },
    [disabled],
  );

  return { ref, panHandlers: { onMouseDown }, isPanning };
}
